import { z } from 'zod';

import {
  AssistantDefinitionState,
  AssistantFirstMessageMode,
  AssistantOptionMode,
  VOICEMAIL_MESSAGE_MAX_LENGTH,
  VoiceCallBackgroundSound,
  VoiceCallIdleMessage,
  VoiceCallLanguage,
} from './constants.js';
import { isDefined } from '../../../utils.js';
import { type BaseItem, type NullablePartial } from '../../common.js';
import { type LlmModel } from '../llmModel.js';
import { type ToolDefinition, ToolDefinitionUpsertValuesSchema } from '../toolDefinition/index.js';
import { type VoicePersona } from '../voicePersona.js';

export * from './constants.js';

export enum StructuredOutputSchemaType {
  String = 'string',
  Boolean = 'boolean',
}

export const StructuredOutputSchemaSchema = z.object({
  type: z.nativeEnum(StructuredOutputSchemaType),
  description: z.string(),
});

export const StructuredOutputUpsertValuesSchema = z.object({
  id: z.string().optional(),
  organizationId: z.string().optional(),
  name: z.string(),
  description: z.string().nullable(),
  schema: StructuredOutputSchemaSchema,
});

export const BaseAssistantDefinitionSchema = z.object({
  assistantId: z.string(),
  prompt: z.string(),
  firstMessage: z.string(),
  firstMessageMode: z.nativeEnum(AssistantFirstMessageMode),
  llmModelId: z.string(),
  modelTemperature: z.number().min(0).max(2),
  language: z.nativeEnum(VoiceCallLanguage),
  voicePersonaId: z.string(),
  voiceSimilarityBoost: z.number().min(0).max(1),
  voiceSpeed: z.number(),
  voiceStability: z.number().min(0).max(1),
  enableSsmlParsing: z.nativeEnum(AssistantOptionMode),
  emotionRecognition: z.nativeEnum(AssistantOptionMode),
  backgroundDenoising: z.nativeEnum(AssistantOptionMode),
  backgroundSound: z.nativeEnum(VoiceCallBackgroundSound),
  startSpeakingSmartEndpointing: z.nativeEnum(AssistantOptionMode),
  startSpeakingWaitSeconds: z.number().min(0).max(5),
  idleMessages: z.array(z.nativeEnum(VoiceCallIdleMessage)),
  idleMessageMaxSpokenCount: z.number().min(1).max(10),
  idleTimeoutSeconds: z.number().min(5).max(60),
  stopSpeakingNumberOfWords: z.number().min(0).max(10),
  stopSpeakingVoiceSeconds: z.number().min(0).max(0.5),
  stopSpeakingVoiceBackOffSeconds: z.number().min(0).max(10),
  maxCallDurationMilliseconds: z
    .number()
    .min(0)
    .max(60 * 60 * 1000),
  /**
   * Nullable fields
   */
  preAssistantMessage: z.string().nullable(),
  voicemailMessage: z.string().max(VOICEMAIL_MESSAGE_MAX_LENGTH).nullable(),
  structuredOutputs: z
    .array(StructuredOutputUpsertValuesSchema)
    .nullable()
    .transform((val) => (val?.length ? val : null)),
  /**
   * In MVP, assistant definition have only one version: 1
   */
  version: z.literal(1),
  /**
   * In MVP, assistant definition have only one state: Published
   */
  state: z.literal(AssistantDefinitionState.Published),
  /**
   * In MVP, assistant definition have no supersededById
   */
  supersededById: z.null(),
  practiceId: z.string(),
});
type BaseAssistantDefinition = z.infer<typeof BaseAssistantDefinitionSchema>;

const _WithToolDefinitionUpsertValuesSchema = z.object({
  toolDefinitions: z
    .array(ToolDefinitionUpsertValuesSchema)
    .nullish()
    .refine(
      (toolDefinitions) => {
        if (!isDefined(toolDefinitions)) {
          return true;
        }
        const types = toolDefinitions.map((toolDefinition) => toolDefinition.type);
        return types.length === new Set(types).size;
      },
      {
        message: 'All tool definitions must have a unique type',
      }
    ),
});
type WithToolDefinitionUpsertValues = z.infer<typeof _WithToolDefinitionUpsertValuesSchema>;

export type AssistantDefinition = BaseItem & BaseAssistantDefinition;

export type UpdateValues = NullablePartial<
  Omit<BaseAssistantDefinition, 'version' | 'state' | 'supersededById' | 'assistantId'> &
    WithToolDefinitionUpsertValues
>;

export type StructuredOutput = Required<z.infer<typeof StructuredOutputUpsertValuesSchema>>;

export type PopulatedAssistantDefinition = AssistantDefinition & {
  llmModel: LlmModel;
  voicePersona: VoicePersona;
  structuredOutputs: StructuredOutput[];
  toolDefinitions: ToolDefinition[];
};
